import { Text } from "@fluentui/react-northstar";
import { PaymentFrequency } from "@teams/types";
import { CouponDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

type Props = {
  planName: string;
  price: number;
  coupon: CouponDTO | undefined;
  taxPercent: number | undefined;
  licenses: number;
  frequency: PaymentFrequency;
  showCoupon: boolean;
  onCalculateTotal?: (total: number) => void;
};

export const LicensedPricingTable: React.FC<Props> = ({
  price,
  planName,
  taxPercent,
  coupon,
  licenses,
  frequency,
  showCoupon,
  onCalculateTotal = () => {},
}) => {
  const numFormat = getCurrencyFormat();
  const [discount, setDiscount] = useState<number>();
  const [total, setTotal] = useState<number>(price);
  const [planTotal, setPlanTotal] = useState<number>(price);
  const [taxAmmount, setTaxAmount] = useState<number>();

  useEffect(() => {
    const pt = price * licenses;
    setPlanTotal(pt);
    const ta = (taxPercent || 0) * pt;
    if (taxPercent !== undefined) {
      setTaxAmount(ta);
    }
    let beforeTotal = pt + ta;

    if (coupon) {
      let discount: number | undefined;
      if (coupon.percentOff) {
        discount = beforeTotal * (coupon.percentOff * 0.01);
      } else if (coupon.amountOff) {
        discount = coupon.amountOff * 0.01;
      }

      if (discount) {
        setDiscount(discount);
        beforeTotal = beforeTotal - discount;
      }
    }

    setTotal(beforeTotal);
    onCalculateTotal(beforeTotal);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTotal, coupon, price, licenses, onCalculateTotal, taxPercent]);

  return (
    <div>
      <table style={{ width: "100%" }}>
        <tbody>
          <tr>
            <td style={{ width: "100%" }}>
              {licenses} {planName} Licenses @ {numFormat.format(price)}/
              {frequency === "semi_annually" ? "semi-annually" : frequency}
            </td>
            <td align="right">{numFormat.format(planTotal)}</td>
          </tr>

          {coupon && discount && (
            <tr>
              <td>
                Coupon {showCoupon && <b>{coupon.name}</b>}: (
                <>
                  {coupon.percentOff > 0 && <Text content={`${coupon.percentOff}% Off`} />}
                  {coupon.amountOff > 0 && (
                    <Text content={`${numFormat.format(coupon.amountOff * 0.01)} Off`} />
                  )}
                </>
                )
              </td>
              <td align="right">{numFormat.format(discount * -1)}</td>
            </tr>
          )}
          <tr>
            <td>Tax: {taxPercent === undefined ? null : `${taxPercent * 100}%`}</td>
            <td align="right">{taxAmmount === undefined ? "?" : numFormat.format(taxAmmount)}</td>
          </tr>
          <tr>
            <td>Total:</td>
            <td align="right">{numFormat.format(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const getCurrencyFormat = () => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
};
